import api from '@/services/apiService';

export interface ProfileResponse {
  data: DataProfile;
  message: string;
  success: boolean;
}

export interface SportCategoryStatItem {
  sport_category_id?: number;
  sport_category_slug?: string;
  sport_category_name?: string;
  sport_category_icon?: string;
  games?: number;
  total_match?: number;
  goals?: number;
  assists?: number;
  clean_sheets?: number;
  saves?: number;
  fouls?: number;
  win_rate?: number;
}

export interface EventHistoryItem {
  id: number;
  name: string;
  slug: string;
  event_date: string;
  city: string;
  lokasi_arena?: string;
  address?: string;
  badge: string;
  badge_bg: string;
  is_sparring: boolean;
  sport_category_name?: string;
  sport_category_slug?: string;
  status: string;
  host_name?: string;
  host_avatar?: string;
}

export interface DataProfile {
  name: string;
  username: string;
  email: string;
  phone: string;
  bio: string;
  birthdate: string;
  birthplace: string;
  gender: string;
  domicile: string;
  profile_image: string;
  profile_type?: 'invasion' | 'racket';
  team_name?: string;
  information?: {
    position?: string;
    dominant_foot?: string;
    height?: number | string;
    location_code?: string;
    location_name?: string;
    gallery?: string[];
    [key: string]: any;
  };
  assets?: {
    gallery?: string[];
    [key: string]: any;
  };
  stats?: {
    events: number;
    matches: number;
    win_rate: number;
  };
  sport_stats?: Record<string, SportCategoryStatItem>;
  event_history?: EventHistoryItem[];
}

const getClientProfile = async (): Promise<ProfileResponse> => {
  const response = await api.get(`/client/profile`);
  return response.data;
};

const getClientEventHistory = async (
  type?: 'mabar' | 'sparing',
  limit: number = 3,
  sportType?: string
): Promise<{ success: boolean; data: EventHistoryItem[] }> => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  params.append('limit', String(limit));
  if (sportType) params.append('sport_type', sportType);
  const response = await api.get(`/client/events/history?${params.toString()}`);
  return response.data;
};

const updateClientProfile = async (data: {
  name?: string;
  username?: string;
  phone?: string;
  bio?: string;
  birth_date?: string;
  birth_place?: string;
  gender?: string;
  domicile?: string;
  profile_type?: 'invasion' | 'racket';
  information?: Record<string, any>;
  profile_image?: File | null;
  profileImageFile?: File | null;
  galleryFiles?: File[];
  keepGallery?: string[];
}) => {
  const hasFiles = !!(
    data.profile_image ||
    data.profileImageFile ||
    (data.galleryFiles && data.galleryFiles.length > 0) ||
    data.keepGallery !== undefined
  );

  if (!hasFiles) {
    const payload: Record<string, any> = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.username !== undefined) payload.username = data.username;
    if (data.phone !== undefined) payload.phone = data.phone;
    if (data.bio !== undefined) payload.bio = data.bio;
    if (data.birth_date !== undefined) payload.birth_date = data.birth_date;
    if (data.birth_place !== undefined) payload.birth_place = data.birth_place;
    if (data.gender !== undefined) payload.gender = data.gender;
    if (data.domicile !== undefined) payload.domicile = data.domicile;
    if (data.profile_type !== undefined) payload.profile_type = data.profile_type;
    if (data.information !== undefined) payload.information = data.information;

    const response = await api.put('/client/profile', payload);
    return response.data;
  }

  const form = new FormData();

  const fieldMap: Array<[string, any]> = [
    ['name', data.name],
    ['username', data.username],
    ['phone', data.phone],
    ['bio', data.bio],
    ['birth_date', data.birth_date],
    ['birth_place', data.birth_place],
    ['gender', data.gender],
    ['domicile', data.domicile],
    ['profile_type', data.profile_type],
  ];

  fieldMap.forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== '') form.append(key, String(val));
  });

  if (data.information) {
    form.append('information', JSON.stringify(data.information));
  }

  if (data.keepGallery !== undefined) {
    form.append('keep_gallery', JSON.stringify(data.keepGallery));
  }

  const file = data.profile_image ?? data.profileImageFile ?? null;
  if (file) {
    const fileName = file.name && file.name.includes('.') ? file.name : `avatar-${Date.now()}.jpg`;
    form.append('profile_image', file, fileName);
  }

  if (data.galleryFiles && data.galleryFiles.length > 0) {
    data.galleryFiles.forEach((gf, index) => {
      const fileName = gf.name && gf.name.includes('.') ? gf.name : `gallery-${Date.now()}-${index}.jpg`;
      form.append('gallery', gf, fileName);
    });
  }

  const response = await api.put(`/client/profile`, form, { timeout: 120000 });
  return response.data;
};

export { getClientProfile, getClientEventHistory, updateClientProfile };
